/*
 * Разделы и экраны «приложения» аналитики на телефоне (AnalyticsApp).
 *
 * Один список на всех: по нему оболочка рисует нижнюю панель, заголовок
 * экрана и «2/3», а WorkoutStats и WorkoutPoints — страницы листалки (ключи
 * экранов ниже совпадают с ключами, которые они рисуют). Число экранов
 * раздела не зависит от данных: пустой график остаётся экраном со своим
 * текстом «пока нет данных», поэтому ?s= из ссылки всегда ведёт туда же.
 *
 * Подписи — русский и английский; остальные языки получают английский, как
 * и сами компоненты (lang === "ru" ? ru : en).
 */
import { shellStateFrom } from "@/features/appShell/model/shellRoutes";

export type AppSectionKey = "overview" | "progress" | "activity" | "practices" | "points" | "nutrition";

type L = { ru: string; en: string };

export interface AppScreenDef {
    key: string;
    title: L;
    /** Заголовок экрана у администраторов, если у них на нём другое (предпросмотр v5). */
    adminTitle?: L;
}

export interface AppSectionDef {
    key: AppSectionKey;
    label: L;
    screens: AppScreenDef[];
}

export const APP_SECTIONS: AppSectionDef[] = [
    {
        key: "overview",
        label: { ru: "Обзор", en: "Overview" },
        screens: [
            { key: "summary", title: { ru: "Сводка", en: "Summary" } },
            { key: "records", title: { ru: "Рекорды", en: "Records" } },
        ],
    },
    {
        key: "progress",
        label: { ru: "Прогресс", en: "Progress" },
        screens: [
            { key: "layers", title: { ru: "Слои очищения", en: "Cleansing layers" } },
            { key: "speed", title: { ru: "Скорость бега", en: "Running speed" } },
        ],
    },
    {
        key: "activity",
        label: { ru: "Активность", en: "Activity" },
        // «Ритм недели» — небольшой второй график на экране «По неделям»:
        // отдельным экраном он был семью столбиками во всю высоту
        screens: [
            // Тепловая карта дней. Ключ (?s=calendar) прежний, а заголовок — не
            // «Календарь»: календаря на сайте нет (владелец, 03.10).
            { key: "calendar", title: { ru: "По дням", en: "By day" } },
            { key: "weekly", title: { ru: "По неделям", en: "By week" } },
        ],
    },
    {
        key: "practices",
        label: { ru: "Практики", en: "Practices" },
        screens: [
            { key: "consistency", title: { ru: "Регулярность", en: "Consistency" } },
            { key: "types", title: { ru: "Виды тренировок", en: "Workout types" } },
        ],
    },
    {
        key: "points",
        label: { ru: "Баллы", en: "Points" },
        // Сводка («Средний балл», «Баллы за неделю», советы) — предпоследним
        // экраном, перед «Правилами», по решению владельца (28.09.2026).
        screens: [
            { key: "weekly", title: { ru: "По неделям", en: "By week" } },
            { key: "history", title: { ru: "История оценок", en: "Score history" } },
            { key: "practices", title: { ru: "По практикам", en: "By practice" } },
            // точность попадания по всем тренировкам и калибровка целей по
            // практикам — обе про «достигнуто / цель», поэтому на одном экране
            { key: "calibration", title: { ru: "Попадание в цель", en: "Target accuracy" } },
            { key: "losses", title: { ru: "Куда уходят баллы", en: "Where points go" } },
            { key: "summary", title: { ru: "Баллы", en: "Points" } },
            // у администраторов здесь «Как получить баллы» (WorkoutPoints → PointsHowTo)
            {
                key: "rules",
                title: { ru: "Правила", en: "Rules" },
                adminTitle: { ru: "Как получить баллы", en: "How to earn points" },
            },
        ],
    },
    {
        // «Питание» (NutritionStats) — не у всех: только кому виден дневник еды и у кого есть диагностика диеты
        // (useNutritionInsights). Нижняя панель рисует его, только если он в sections (AppTabBar).
        key: "nutrition",
        label: { ru: "Питание", en: "Nutrition" },
        screens: [
            { key: "today", title: { ru: "Сегодня", en: "Today" } },
            { key: "calories", title: { ru: "Калории", en: "Calories" } },
            { key: "suits", title: { ru: "Что подходит", en: "What suits you" } },
            { key: "swaps", title: { ru: "Чем заменить", en: "What to eat instead" } },
            { key: "workouts", title: { ru: "Сожжено на тренировках", en: "Burned in workouts" } },
        ],
    },
];

/** Разделы, которые есть у всех (без «Питания»). */
export const BASE_SECTIONS: readonly AppSectionKey[] = ["overview", "progress", "activity", "practices", "points"];

/** Разделы этого человека: «Питание» — если оно ему есть. */
export const sectionsWith = (nutrition: boolean): readonly AppSectionKey[] =>
    nutrition ? [...BASE_SECTIONS, "nutrition"] : BASE_SECTIONS;

/** Разделы, которые рисует WorkoutStats (всё, кроме баллов). */
export const STATS_SECTIONS: AppSectionKey[] = ["overview", "progress", "activity", "practices"];

export const pickLang = (l: L, lang: string): string => (lang === "ru" ? l.ru : l.en);

export const sectionDef = (key: AppSectionKey): AppSectionDef =>
    APP_SECTIONS.find((s) => s.key === key) || APP_SECTIONS[0];

/** Номер экрана раздела по его ключу (0, если такого нет): ссылки не зависят от порядка. */
export const screenIndex = (section: AppSectionKey, screen: string): number =>
    Math.max(0, sectionDef(section).screens.findIndex((s) => s.key === screen));

/**
 * ?tab= → раздел. «points» — прежняя ссылка на вкладку «Баллы» и новый
 * раздел одновременно; «analytics», пусто, всё незнакомое и раздел, которого
 * у человека нет (available: «Питание» не у всех), — «Обзор».
 */
export const sectionFromParam = (tab: string | null, available: readonly AppSectionKey[] = BASE_SECTIONS): AppSectionKey => {
    const hit = APP_SECTIONS.find((s) => s.key === tab && available.includes(s.key));
    return hit ? hit.key : "overview";
};

/**
 * Что страница лиги участников получает от вкладки «Лига» (location.state):
 * раздел и экран аналитики, на который вернуть «‹» лиги. Лига встаёт на место
 * записи истории аналитики (replace), как смена раздела внутри приложения.
 */
export interface LeagueFromAnalytics {
    /** «?tab=points&s=6» */
    analyticsBack: string;
    /**
     * Телефон «как приложение» (оболочка): вкладка нижней панели, которую
     * заменил слой аналитики (её state.from). Лига отдаёт её обратно
     * аналитике — «‹» аналитики вернёт на эту вкладку.
     */
    from?: string;
}

export const leagueBackState = (section: AppSectionKey, screen: number, from?: string | null): LeagueFromAnalytics => ({
    analyticsBack: `?tab=${section}&s=${screen + 1}`,
    ...(from ? { from } : {}),
});

/** analyticsBack из location.state, если лигу открыли вкладкой аналитики. */
export const analyticsBackOf = (state: unknown): string | null => {
    const v = state && typeof state === "object" ? (state as Partial<LeagueFromAnalytics>).analyticsBack : undefined;
    return typeof v === "string" && v.startsWith("?") ? v : null;
};

/**
 * Что страница лиги получает, когда «Прогресс и лига» открылся сразу ею
 * (вход кнопкой без раздела в адресе — leagueFirst.ts). analyticsBack нет:
 * «Обзора» человек не видел, и «‹» лиги — выход из «Прогресс и лига», как
 * «‹» аналитики. from — вкладка оболочки, которую заменил слой (только в
 * оболочке): «‹» лиги вернёт на неё, разделы нижней панели передадут её
 * аналитике.
 */
export interface LeagueFirstState {
    leagueFirst: true;
    from?: string;
}

export const leagueFirstState = (from?: string | null): LeagueFirstState => ({
    leagueFirst: true,
    ...(from ? { from } : {}),
});

/** Лигу открыл вход в «Прогресс и лига» (а не вкладка «Лига» и не ссылка). */
export const isLeagueFirst = (state: unknown): boolean =>
    !!state && typeof state === "object" && (state as Partial<LeagueFirstState>).leagueFirst === true;

/**
 * Вкладка оболочки (state.from), которую заменил слой аналитики, — только если
 * лигу открыли из аналитики или ею открылся «Прогресс и лига».
 */
export const analyticsTabFromOf = (state: unknown): string | null =>
    analyticsBackOf(state) || isLeagueFirst(state) ? shellStateFrom(state) : null;

/**
 * Что показывает компонент в режиме приложения: раздел (null — его разделы
 * сейчас не на экране: компонент остаётся смонтированным с загруженными
 * данными, но ничего не рисует), номер экрана с нуля и как его сменить;
 * go — перейти на экран другого раздела (плитки сводки ведут к своему графику).
 */
export interface AnalyticsAppView {
    section: AppSectionKey | null;
    screen: number;
    onScreen: (index: number) => void;
    go: (section: AppSectionKey, screen: number) => void;
}
